import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Search, MessageCircle, Phone, ChevronDown, MapPin, CircleHelp, Menu, X, Star,
  Baby, ToyBrick, Puzzle, Bot, Gamepad2, Dog, Blocks, Dices, Car, PersonStanding, Palette,
  Music, BookOpen, FlaskConical, Cake, ShoppingBag, CreditCard, Sparkles,
} from "lucide-react";
import { navItems, LOGO_URL } from "../mock";
import { fetchProducts, resolveImageUrl } from "../api";

const dropIcons = {
  Baby, ToyBrick, Puzzle, Bot, Gamepad2, Dog, Blocks, Dices, Car, PersonStanding, Palette,
  Music, BookOpen, FlaskConical, Cake, ShoppingBag, CreditCard, Sparkles,
};

const NAVY = "#1B1F5C";

const Header = ({ cartCount = 0, wishlistCount = 0 }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const searchRef = useRef(null);

  // Debounced live search suggestions
  useEffect(() => {
    if (!query.trim() || query.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      fetchProducts({ search: query.trim(), limit: 6 })
        .then((data) => setSuggestions(data))
        .catch(() => setSuggestions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const goToSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setShowSuggestions(false);
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  const goToProduct = (id) => {
    setShowSuggestions(false);
    setQuery("");
    navigate(`/product/${id}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      {/* Top announcement bar */}
      <div className="text-white text-xs" style={{ backgroundColor: NAVY }}>
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-9">
          <div className="flex-1 hidden sm:block" />
          <div className="flex items-center gap-1.5 flex-1 justify-center whitespace-nowrap">
            <Star size={12} className="text-[#FCD44E] fill-[#FCD44E]" />
            <span className="font-semibold">Spreading Joy, <span className="text-[#FCD44E]">One Toy at a Time!</span></span>
          </div>
          <div className="flex-1 hidden md:flex items-center justify-end gap-4 text-[11px] whitespace-nowrap">
            <a href="tel:+918828131962" className="flex items-center gap-1 hover:text-[#FCD44E] transition-colors whitespace-nowrap" data-testid="topbar-phone-link"><Phone size={11} /> +91 88281 31962</a>
            <span className="opacity-30">|</span>
            <Link to="/store-locator" className="flex items-center gap-1 hover:text-[#FCD44E] transition-colors whitespace-nowrap" data-testid="topbar-store-locator-link"><MapPin size={11} /> Store Locator</Link>
            <span className="opacity-30">|</span>
            <Link to="/help-center" className="flex items-center gap-1 hover:text-[#FCD44E] transition-colors whitespace-nowrap" data-testid="topbar-help-link"><CircleHelp size={11} /> Help Center</Link>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between gap-4 py-2">
          {/* Mobile menu btn */}
          <button className="lg:hidden p-2 -ml-2" style={{ color: NAVY }} onClick={() => setMobileOpen(true)} aria-label="Open menu">
            <Menu size={24} />
          </button>

          {/* Logo */}
          <Link to="/" className="shrink-0 flex items-center">
            <img src={LOGO_URL} alt="Adhira & Appa Toys" className="h-14 md:h-16 w-auto object-contain" />
          </Link>

          {/* Search */}
          <div className="hidden md:flex flex-1 max-w-xl mx-4" ref={searchRef}>
            <div className="relative w-full">
              <form onSubmit={goToSearch}>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  placeholder="Search for toys, games, brands & more..."
                  className="w-full h-11 pl-5 pr-12 rounded-full border border-gray-200 bg-gray-50 text-sm outline-none focus:border-[#35C3C1] focus:ring-2 focus:ring-[#35C3C1]/20 transition-colors"
                />
                <button type="submit" className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full flex items-center justify-center" style={{ color: NAVY }} aria-label="Search">
                  <Search size={19} />
                </button>
              </form>
              {/* Suggestions dropdown */}
              {showSuggestions && query.trim().length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                  {suggestions.length > 0 ? (
                    <>
                      {suggestions.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => goToProduct(p.id)}
                          className="flex items-center gap-3 w-full px-4 py-2.5 text-left hover:bg-[#F7F7FA] transition-colors"
                        >
                          <span className="h-11 w-11 rounded-lg bg-[#F7F7FA] shrink-0 overflow-hidden flex items-center justify-center">
                            {p.image ? <img src={resolveImageUrl(p.image)} alt={p.name} className="h-full w-full object-contain mix-blend-multiply" /> : <Search size={15} className="text-gray-300" />}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-[13px] font-bold truncate" style={{ color: NAVY }}>{p.name}</span>
                            <span className="block text-[11.5px] font-semibold text-gray-400">
                              {p.brand || "Adhira & Appa"}{p.age ? ` · ${p.age}` : ""}
                            </span>
                          </span>
                        </button>
                      ))}
                      <button onClick={goToSearch} className="w-full px-4 py-3 text-[12px] font-extrabold text-[#35C3C1] hover:bg-[#F7F7FA] transition-colors border-t border-gray-100">
                        View all results for “{query.trim()}”
                      </button>
                    </>
                  ) : (
                    <p className="px-4 py-4 text-[12.5px] font-semibold text-gray-400">No toys found for “{query.trim()}”</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 md:gap-6">
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("open-enquiry"))}
              data-testid="header-enquire-btn"
              className="inline-flex items-center gap-2 h-10 px-4 md:px-5 rounded-full text-white text-[11.5px] font-extrabold tracking-wide shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-transform duration-200"
              style={{ backgroundColor: NAVY }}
            >
              <MessageCircle size={15} />
              <span className="hidden sm:inline">ENQUIRE NOW</span>
              <span className="sm:hidden">ENQUIRE</span>
            </button>
          </div>
        </div>

        {/* Mobile search */}
        <div className="md:hidden pb-3">
          <form onSubmit={goToSearch} className="relative w-full">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for toys, games & more..."
              className="w-full h-10 pl-4 pr-11 rounded-full border border-gray-200 bg-gray-50 text-sm outline-none focus:border-[#35C3C1]"
            />
            <button type="submit" className="absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full flex items-center justify-center" style={{ color: NAVY }} aria-label="Search">
              <Search size={17} />
            </button>
          </form>
        </div>
      </div>

      {/* Desktop nav */}
      <nav className="hidden lg:block border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <ul className="flex items-center justify-center gap-10 h-12">
            {navItems.map((item) => {
              const isActive = item.path && location.pathname === item.path;
              const activeColor = item.activeColor || "#35C3C1";
              const Label = item.path ? (
                <Link
                  to={item.path}
                  className="flex items-center gap-1 text-[14px] font-bold transition-colors relative h-full"
                  style={{ color: isActive ? activeColor : NAVY }}
                >
                  {item.label}
                  {item.dropdown && <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-200" />}
                  {isActive && <span className="absolute -bottom-2 left-0 right-0 h-[3px] rounded-full" style={{ backgroundColor: activeColor }} />}
                </Link>
              ) : (
                <button className="flex items-center gap-1 text-[14px] font-bold hover:text-[#35C3C1] transition-colors" style={{ color: NAVY }}>
                  {item.label}
                  {item.dropdown && <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-200" />}
                </button>
              );
              return (
                <li key={item.label} className="relative group h-full flex items-center">
                  {Label}
                  {item.dropdown && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-150 z-50">
                      <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[220px]">
                        {item.dropdown.map((d) => {
                          const DIcon = dropIcons[d.icon];
                          return item.path ? (
                            <Link key={d.label} to={`${item.path}?${item.path === "/shop-by-age" ? "age" : "sub"}=${encodeURIComponent(d.label)}`} className="flex items-center gap-3 w-full text-left px-5 py-2.5 text-[13px] font-semibold text-gray-600 hover:text-[#1B1F5C] hover:bg-[#FCD44E]/20 transition-colors">
                              {DIcon && (
                                <span className="h-7 w-7 rounded-full bg-[#EEF0FA] flex items-center justify-center shrink-0">
                                  <DIcon size={14} style={{ color: NAVY }} strokeWidth={1.8} />
                                </span>
                              )}
                              {d.label}
                            </Link>
                          ) : (
                            <button key={d.label} className="flex items-center gap-3 w-full text-left px-5 py-2.5 text-[13px] font-semibold text-gray-600 hover:text-[#1B1F5C] hover:bg-[#FCD44E]/20 transition-colors">
                              {DIcon && (
                                <span className="h-7 w-7 rounded-full bg-[#EEF0FA] flex items-center justify-center shrink-0">
                                  <DIcon size={14} style={{ color: NAVY }} strokeWidth={1.8} />
                                </span>
                              )}
                              {d.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[300px] bg-white shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <img src={LOGO_URL} alt="Adhira & Appa Toys" className="h-12 w-auto" />
              <button onClick={() => setMobileOpen(false)} style={{ color: NAVY }} aria-label="Close menu"><X size={24} /></button>
            </div>
            <div className="p-4">
              {navItems.map((item, i) => (
                <div key={item.label} className="border-b border-gray-100">
                  <div className="flex items-center justify-between w-full py-3.5">
                    {item.path ? (
                      <Link
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        className="text-[15px] font-bold"
                        style={{ color: location.pathname === item.path ? (item.activeColor || "#35C3C1") : NAVY }}
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <span className="text-[15px] font-bold" style={{ color: NAVY }}>{item.label}</span>
                    )}
                    {item.dropdown && (
                      <button onClick={() => setOpenAccordion(openAccordion === i ? null : i)} aria-label={`Expand ${item.label}`} style={{ color: NAVY }}>
                        <ChevronDown size={16} className={`transition-transform ${openAccordion === i ? "rotate-180" : ""}`} />
                      </button>
                    )}
                  </div>
                  {item.dropdown && openAccordion === i && (
                    <div className="pb-3 pl-3">
                      {item.dropdown.map((d) => {
                        const DIcon = dropIcons[d.icon];
                        return (
                          <Link
                            key={d.label}
                            to={item.path ? `${item.path}?${item.path === "/shop-by-age" ? "age" : "sub"}=${encodeURIComponent(d.label)}` : "/"}
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center gap-2.5 w-full text-left py-2 text-[13px] font-semibold text-gray-600"
                          >
                            {DIcon && (
                              <span className="h-6 w-6 rounded-full bg-[#EEF0FA] flex items-center justify-center shrink-0">
                                <DIcon size={13} style={{ color: NAVY }} strokeWidth={1.8} />
                              </span>
                            )}
                            {d.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
              <div className="mt-5 space-y-3 text-[13px] font-semibold" style={{ color: NAVY }}>
                <button
                  onClick={() => { setMobileOpen(false); window.dispatchEvent(new CustomEvent("open-enquiry")); }}
                  data-testid="mobile-enquire-btn"
                  className="flex items-center gap-2"
                >
                  <MessageCircle size={16} /> Enquire Now
                </button>
                <a href="tel:+918828131962" className="flex items-center gap-2" data-testid="mobile-call-link"><Phone size={16} /> +91 88281 31962</a>
                <Link to="/store-locator" onClick={() => setMobileOpen(false)} className="flex items-center gap-2"><MapPin size={16} /> Store Locator</Link>
                <Link to="/help-center" onClick={() => setMobileOpen(false)} className="flex items-center gap-2"><CircleHelp size={16} /> Help Center</Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
